import * as RadixSlider from '@radix-ui/react-slider';
import { wrap, top, value as valueCls, perfectValue, root, track, range, thumb, perfectThumb, bottom, minmax } from './Slider.css';

interface SliderProps {
  label: string;
  /** [value] for single, [min,max] for range, [min,perfect,max] for range+sweet point. */
  value: number[];
  min: number;
  max: number;
  step?: number;
  onChange: (value: number[]) => void;
  format?: (v: number) => string;
  /** Bottom-left label; defaults to format(min). Pass null to hide the min/max footer. */
  minLabel?: string | null;
  /** Bottom-right label; defaults to format(max). */
  maxLabel?: string;
  /** Index of the "sweet point" thumb (styled distinctly), if present. */
  perfectIndex?: number;
  /** Label for the sweet-point value, shown alongside the range value. */
  perfectLabel?: string;
}

export function Slider({ label, value, min, max, step = 1, onChange, format = (v) => String(v), minLabel, maxLabel, perfectIndex, perfectLabel }: SliderProps) {
  const hasPerfect = perfectIndex != null;
  // With a perfect point the range is the two outer thumbs; otherwise it's the
  // whole array (single value or a plain [min,max] range).
  const rangeVals = hasPerfect ? value.filter((_, i) => i !== perfectIndex) : value;
  const rangeText = rangeVals.length === 2 ? `${format(rangeVals[0])} – ${format(rangeVals[1])}` : format(rangeVals[0]);
  const showFooter = minLabel !== null;
  return (
    <div className={wrap}>
      {/* The section title already names this slider; show only the value, left-aligned.
          The sweet point sits in its own span, padded away from the range value. */}
      <div className={top}>
        <span className={valueCls}>{rangeText}</span>
        {hasPerfect && (
          <span className={perfectValue}>{`${perfectLabel ?? 'Sweet point'} ${format(value[perfectIndex])}`}</span>
        )}
      </div>
      <RadixSlider.Root className={root} value={value} min={min} max={max} step={step}
                        onValueChange={onChange} aria-label={label}>
        <RadixSlider.Track className={track}>
          <RadixSlider.Range className={range} />
        </RadixSlider.Track>
        {value.map((_, i) => (
          <RadixSlider.Thumb
            key={i}
            className={i === perfectIndex ? perfectThumb : thumb}
            aria-label={i === perfectIndex ? (perfectLabel ?? 'Sweet point') : label}
          />
        ))}
      </RadixSlider.Root>
      {showFooter && (
        <div className={bottom}>
          <span className={minmax}>{minLabel ?? format(min)}</span>
          <span className={minmax}>{maxLabel ?? format(max)}</span>
        </div>
      )}
    </div>
  );
}
